import { Icon, Input, type InputDomRef } from "@ui5/webcomponents-react";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import "@ui5/webcomponents-icons/dist/decline.js";
import "@ui5/webcomponents-icons/dist/slim-arrow-down.js";

import { handled, nativeInputOf } from "./smartField";
import { OptionText, SmartList } from "./SmartList";
import { exactOption, type SmartOption } from "./smartMatch";
import { defaultMaxShown, useSmartOptions, type SmartSearch } from "./useSmartOptions";
import "./smartSelect.css";

export type SmartValueState = "None" | "Negative" | "Critical" | "Positive" | "Information";

/** Props shared by `SmartSelect` and `SmartMultiSelect`. */
export interface SmartFieldProps {
  /** Id of the field's input (a `Label for` points at it). */
  id?: string;
  options: readonly SmartOption[];
  /** Server search instead of filtering `options` (large entities); `options` then only name chosen values. */
  search?: SmartSearch;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  valueState?: SmartValueState;
  /** Shown under the field while it is focused and not in state "None" (as UI5's `valueStateMessage`). */
  valueStateMessage?: ReactNode;
  accessibleName?: string;
  /** Rows listed at most (default 100); typing narrows the rest. */
  maxShown?: number;
  className?: string;
  style?: CSSProperties;
  "data-testid"?: string;
}

export interface SmartSelectProps extends SmartFieldProps {
  value: string;
  /** The picked value, with its option (`undefined` when the choice is cleared). */
  onChange: (value: string, option?: SmartOption) => void;
  /** Label of the empty choice (e.g. "All", "None"): listed first and shown as the placeholder when nothing is chosen. */
  emptyLabel?: string;
  /** Whether the choice can be removed (clear button, emptied text). Default: when not required or with `emptyLabel`. */
  clearable?: boolean;
  /** Label of the current value when it is not among `options` (a server-searched record). */
  selectedLabel?: string;
}

/**
 * A choice among records (accounts, items, parties, warehouses…) found by typing (UX12): part of the code or of the
 * Arabic / English name, several words in any order, Arabic spelling variants and Arabic-Indic digits matching; the
 * matching text is highlighted. The arrows move through the list, Enter (or Tab) picks, Esc restores the choice, the
 * clear button (or emptying the text) removes it. Text that matches no record is not kept. Long lists render their
 * first `maxShown` matches; `search` asks the server instead (items, parties).
 */
export function SmartSelect({
  id,
  value,
  options,
  onChange,
  search,
  emptyLabel,
  clearable,
  selectedLabel,
  placeholder,
  required = false,
  disabled = false,
  readonly = false,
  valueState = "None",
  valueStateMessage,
  accessibleName,
  maxShown = defaultMaxShown,
  className,
  style,
  "data-testid": testId,
}: SmartSelectProps) {
  const { t } = useTranslation("ui");
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputDomRef>(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  /** The typed text while searching; `null` shows the chosen record's label. */
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(-1);
  const state = useSmartOptions(options, query ?? "", open, search, maxShown);
  const canClear = clearable ?? (!required || emptyLabel !== undefined);
  const editable = !disabled && !readonly;

  const chosen = value === "" ? undefined : state.known.get(value);
  const chosenLabel = value === "" ? "" : (chosen?.label ?? selectedLabel ?? value);
  // An opened list starts from an empty text (every record listed); the chosen label shows as its placeholder.
  const text = query ?? (open ? "" : chosenLabel);
  const typed = query !== null && query.trim() !== "";

  /** Listed rows: the empty choice first (when it exists and nothing is typed), then the matches. */
  const rows = useMemo<readonly SmartOption[]>(
    () => (emptyLabel && !typed ? [{ value: "", label: emptyLabel }, ...state.matches] : state.matches),
    [emptyLabel, typed, state.matches],
  );

  // The highlight follows the list: the chosen record when it opens untouched, the best match while typing. A parent
  // that rebuilds the same options on each render does not move it.
  const rowsKey = `${String(open)}|${query ?? ""}|${String(rows.length)}|${rows[0]?.value ?? ""}`;
  useEffect(() => {
    if (!open) return;
    const start = typed ? 0 : rows.findIndex((row) => row.value === value);
    const index = start >= 0 && !rows[start]?.disabled ? start : rows.findIndex((row) => !row.disabled);
    setActive(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-highlight when the rows change, not on each value
  }, [rowsKey]);

  const close = () => {
    setOpen(false);
    setQuery(null);
  };

  const pick = (option: SmartOption | undefined) => {
    close();
    if (!option || option.disabled) return;
    if (option.value !== value) onChange(option.value, option.value === "" ? undefined : option);
  };

  /** Leaving the field: an emptied text clears the choice, an exact code or label picks it, anything else is undone. */
  const settle = () => {
    if (query === null) {
      close();
      return;
    }
    const trimmed = query.trim();
    if (trimmed === "") {
      close();
      if (canClear && value !== "") onChange("");
      return;
    }
    pick(exactOption(search ? state.matches : options, trimmed));
  };

  const openList = () => {
    if (!editable) return;
    setOpen(true);
  };

  const move = (step: number) => {
    if (rows.length === 0) return;
    setActive((index) => {
      let next = Math.min(Math.max(index + step, 0), rows.length - 1);
      // Skip disabled rows in the direction of the move; stay put when only disabled rows remain.
      while (rows[next]?.disabled && next > 0 && next < rows.length - 1) next += Math.sign(step);
      return rows[next]?.disabled ? index : next;
    });
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!editable) return;
    const { key } = event;
    if (key === "ArrowDown" || key === "ArrowUp" || key === "PageDown" || key === "PageUp") {
      handled(event);
      if (!open) {
        openList();
        return;
      }
      move(key === "ArrowDown" ? 1 : key === "ArrowUp" ? -1 : key === "PageDown" ? 10 : -10);
    } else if (key === "F4") {
      handled(event);
      if (open) close();
      else openList();
    } else if (key === "Enter") {
      if (open && active >= 0) {
        handled(event);
        pick(rows[active]);
      } else if (query !== null) {
        handled(event);
        settle();
      }
    } else if (key === "Escape") {
      if (open || query !== null) {
        handled(event);
        close();
      }
      // UI5's input puts back its own previous text on Escape; show the chosen record again.
      window.setTimeout(() => {
        if (inputRef.current && inputRef.current.value !== chosenLabel) inputRef.current.value = chosenLabel;
      }, 0);
    } else if (key === "Tab") {
      if (open && typed && active >= 0) pick(rows[active]);
      else settle();
    }
  };

  const icons: ReactElement[] = [];
  if (editable && canClear && value !== "") {
    icons.push(
      <Icon
        key="clear"
        name="decline"
        className="positive-smart-icon"
        accessibleName={t("select.clear")}
        showTooltip
        data-testid={testId ? `${testId}--clear` : undefined}
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={(event) => {
          event.stopPropagation();
          close();
          onChange("");
        }}
      />,
    );
  }
  if (editable) {
    icons.push(
      <Icon
        key="open"
        name="slim-arrow-down"
        className="positive-smart-icon"
        accessibleName={t("select.open")}
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={(event) => {
          event.stopPropagation();
          nativeInputOf(inputRef.current)?.focus();
          if (open) close();
          else openList();
        }}
      />,
    );
  }

  // An empty choice without a label ("") is no hint: the search hint shows instead.
  const emptyHint = emptyLabel === "" ? undefined : emptyLabel;
  const listLabel = accessibleName ?? t("select.suggestions");
  const more = state.total - state.matches.length;

  return (
    <div
      ref={rootRef}
      className={["positive-smart-select", className].filter(Boolean).join(" ")}
      style={style}
      data-testid={testId}
      data-value={value}
      data-open={open || undefined}
      onBlur={(event) => {
        if (event.relatedTarget instanceof Node && rootRef.current?.contains(event.relatedTarget)) return;
        settle();
      }}
    >
      <Input
        ref={inputRef}
        {...(id === undefined ? {} : { id })}
        value={text}
        placeholder={
          open && chosenLabel !== ""
            ? chosenLabel
            : (placeholder ?? emptyHint ?? (editable ? t("select.searchPlaceholder") : undefined))
        }
        required={required}
        disabled={disabled}
        readonly={readonly}
        valueState={valueState}
        {...(valueStateMessage === undefined ? {} : { valueStateMessage: <div>{valueStateMessage}</div> })}
        accessibleName={accessibleName}
        {...(icons.length > 0 ? { icon: icons } : {})}
        data-testid={testId ? `${testId}--input` : undefined}
        title={chosenLabel === "" ? undefined : chosenLabel}
        onFocus={() => {
          // Typing replaces the shown label instead of adding to it.
          window.setTimeout(() => {
            nativeInputOf(inputRef.current)?.select();
          }, 0);
        }}
        onClick={() => {
          if (!open) openList();
        }}
        onInput={(event) => {
          // UI5 reports its own Escape restore as an input without an input type; Escape is handled above.
          const detail = (event as unknown as { detail?: { inputType?: string } }).detail;
          if (detail?.inputType === "") return;
          setQuery(event.target.value);
          if (editable) setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      {open && editable && (
        <SmartList
          id={listId}
          anchor={rootRef}
          label={listLabel}
          active={active}
          data-testid={testId ? `${testId}--list` : undefined}
          footer={
            more > 0 ? (
              <div className="positive-smart-note" data-testid={testId ? `${testId}--more` : undefined}>
                {t("select.more", { shown: state.matches.length, total: state.total })}
              </div>
            ) : null
          }
        >
          {state.busy && state.matches.length === 0 && (
            <div className="positive-smart-note">{t("select.searching")}</div>
          )}
          {!state.busy && rows.length === 0 && (
            <div className="positive-smart-note">
              {options.length === 0 && !search && !typed ? t("select.noOptions") : t("select.noMatches")}
            </div>
          )}
          {rows.map((option, index) => (
            <div
              key={option.value === "" ? "\u0000empty" : option.value}
              role="option"
              className={option.value === "" ? "positive-smart-option positive-smart-empty" : "positive-smart-option"}
              aria-selected={option.value === value}
              aria-disabled={option.disabled === true ? true : undefined}
              data-value={option.value}
              data-active={index === active || undefined}
              onMouseEnter={() => {
                if (!option.disabled) setActive(index);
              }}
              onClick={() => {
                pick(option);
              }}
            >
              <OptionText option={option} query={typed ? query : ""} />
            </div>
          ))}
        </SmartList>
      )}
    </div>
  );
}
